import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, X, Copy, Link } from 'lucide-react';
import { useToast } from './Toast';
import { eventRef } from '../utils/eventRef';

const GenerateLinkModal = ({ eventId, onClose, formSchema = [] }) => {
  const [params, setParams] = useState([{ key: '', value: '' }]);
  const { showSuccess } = useToast();

  const handleAddParam = () => {
    setParams([...params, { key: '', value: '' }]);
  };

  const handleRemoveParam = (index) => {
    setParams(params.filter((_, i) => i !== index));
  };

  const handleParamChange = (index, field, value) => {
    const newParams = [...params];
    newParams[index][field] = value;
    setParams(newParams);
  };

  const generateLink = () => {
    const baseUrl = `${window.location.origin}/register/${eventRef(eventId)}`;
    const validParams = params.filter(p => p.key && p.value);
    
    if (validParams.length === 0) {
      return baseUrl;
    }

    const queryString = validParams
      .map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`)
      .join('&');
    
    return `${baseUrl}?${queryString}`;
  };

  const copyGeneratedLink = () => {
    const link = generateLink();
    navigator.clipboard.writeText(link);
    showSuccess('Generated link copied to clipboard!');
  };

  // Get form field labels for suggestions
  const formFields = formSchema
    .map(field => field.label)
    .filter(label => label);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Generate Registration Link</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              Add key-value pairs to pre-fill form fields. Available fields:
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {formFields.map((field, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                >
                  {field}
                </span>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {params.map((param, index) => (
              <div key={index} className="flex gap-4 items-start">
                <div className="flex-1">
                  <Label htmlFor={`key-${index}`} className="text-xs">
                    Key
                  </Label>
                  <Input
                    id={`key-${index}`}
                    value={param.key}
                    onChange={(e) => handleParamChange(index, 'key', e.target.value)}
                    placeholder="e.g., Category"
                    list={`suggestions-${index}`}
                  />
                  <datalist id={`suggestions-${index}`}>
                    {formFields.map((field, i) => (
                      <option key={i} value={field} />
                    ))}
                  </datalist>
                </div>
                <div className="flex-1">
                  <Label htmlFor={`value-${index}`} className="text-xs">
                    Value
                  </Label>
                  <Input
                    id={`value-${index}`}
                    value={param.value}
                    onChange={(e) => handleParamChange(index, 'value', e.target.value)}
                    placeholder="e.g., VIP"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveParam(index)}
                  className="mt-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleAddParam}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Parameter
          </Button>

          <div className="pt-4 border-t">
            <Label className="text-sm font-medium">Generated Link:</Label>
            <div className="mt-2 p-3 bg-gray-50 rounded-lg break-all text-sm font-mono">
              {generateLink()}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={copyGeneratedLink}>
              <Copy className="h-4 w-4 mr-2" />
              Copy Link
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GenerateLinkModal;
